import { Component, OnInit, OnDestroy, DestroyRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TableModule, TableRowReorderEvent } from 'primeng/table';
import { Button } from 'primeng/button';
import { Dialog } from 'primeng/dialog';
import { Select } from 'primeng/select';
import { InputText } from 'primeng/inputtext';
import { Checkbox } from 'primeng/checkbox';
import { Tooltip } from 'primeng/tooltip';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { MessageService, ConfirmationService } from 'primeng/api';
import { catchError, of, forkJoin } from 'rxjs';
import { OffboardingService } from '../../services/offboarding.service';
import { OffboardingTaskTemplate, CreateOffboardingTaskTemplate } from '../../models/offboarding.model';
import { PageHeaderSearchActionService } from '../../services/page-header-search-action.service';

// Fixed tab order matching the onboarding page pattern: IT → P&C → Q&A.
type TabKey = 'IT' | 'PnC' | 'QA';

interface TabDef {
  key: TabKey;
  label: string;
  /** Backend `department` value this tab represents */
  department: string;
}

@Component({
  selector: 'app-offboarding-task-templates',
  standalone: true,
  imports: [
    // TableModule bundles p-table + its child directives (pReorderableRow, pReorderableRowHandle, etc.)
    CommonModule, FormsModule, TableModule, Button, Dialog, Select,
    InputText, Checkbox, Tooltip, ConfirmDialog
  ],
  providers: [ConfirmationService],
  templateUrl: './offboarding-task-templates.component.html',
  styleUrl: './offboarding-task-templates.component.scss'
})
export class OffboardingTaskTemplatesComponent implements OnInit, OnDestroy {
  private destroyRef = inject(DestroyRef);

  templates: OffboardingTaskTemplate[] = [];
  loading = true;

  // Fixed tab order: IT → P&C → Q&A
  readonly tabs: TabDef[] = [
    { key: 'IT',  label: 'IT',                    department: 'IT'  },
    { key: 'PnC', label: 'P&C',                   department: 'PnC' },
    { key: 'QA',  label: 'Q&A (Questionnaire)',   department: 'QA'  }
  ];

  activeTab: TabKey = 'IT';

  // Per-tab search + status filter
  tabSearch = '';
  tabStatusFilter: 'all' | 'active' | 'inactive' = 'all';

  statusFilterOptions = [
    { label: 'All Status',    value: 'all' },
    { label: 'Active only',   value: 'active' },
    { label: 'Inactive only', value: 'inactive' }
  ];

  // Dialog state
  displayDialog = false;
  isEditMode = false;
  selectedTemplate: OffboardingTaskTemplate = this.getEmpty();
  fieldTouched: Record<string, boolean> = {};

  // Restrict the Add dialog's Department dropdown to the currently active tab.
  // In edit mode all departments are shown so an admin can move a task between departments.
  dialogDepartmentOptions: { label: string; value: string }[] = [];

  private readonly allDepartmentOptions = [
    { label: 'IT',                  value: 'IT'  },
    { label: 'P&C',                 value: 'PnC' },
    { label: 'Q&A (Questionnaire)', value: 'QA'  }
  ];

  reordering = false;

  // Bulk-delete selection state
  selectedIds: string[] = [];
  deletingBulk = false;

  constructor(
    private offboardingService: OffboardingService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    public pageHeaderService: PageHeaderSearchActionService
  ) {}

  ngOnInit(): void {
    // Global page header: search only. Add and Filter buttons live inside each tab.
    this.pageHeaderService.setCanShowFilterToggle(false);
    this.pageHeaderService.setPlaceholder('Search tasks in this tab…');
    this.pageHeaderService.setCanShowPrimaryAction(false);
    this.pageHeaderService.setCanShowExport(false);

    this.loadTemplates();

    this.pageHeaderService.searchText.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(v => { this.tabSearch = v ?? ''; });
  }

  ngOnDestroy(): void {
    this.pageHeaderService.clearPageConfig();
  }

  loadTemplates(): void {
    this.loading = true;
    this.offboardingService.getAllTaskTemplates()
      .pipe(takeUntilDestroyed(this.destroyRef), catchError(() => of([] as OffboardingTaskTemplate[])))
      .subscribe(data => {
        this.templates = data || [];
        this.loading = false;
      });
  }

  // ================= Tab selection =================

  setTab(key: TabKey): void {
    this.activeTab = key;
    this.tabSearch = '';
    this.tabStatusFilter = 'all';
    this.selectedIds = [];
  }

  getTabCount(key: TabKey): number {
    const def = this.tabs.find(t => t.key === key);
    if (!def) return 0;
    return this.templates.filter(t => t.department === def.department).length;
  }

  // ================= Current tab data =================

  /** Tasks visible in the active tab after applying tab-local search + status filter, sorted by sortOrder. */
  get currentTabTasks(): OffboardingTaskTemplate[] {
    const def = this.tabs.find(t => t.key === this.activeTab);
    if (!def) return [];

    let list = this.templates.filter(t => t.department === def.department);

    const s = this.tabSearch.trim().toLowerCase();
    if (s) list = list.filter(t => t.taskName?.toLowerCase().includes(s));

    if (this.tabStatusFilter === 'active')   list = list.filter(t => t.isDefault);
    if (this.tabStatusFilter === 'inactive') list = list.filter(t => !t.isDefault);

    return [...list].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
  }

  get activeTabLabel(): string {
    return this.tabs.find(t => t.key === this.activeTab)?.label ?? '';
  }

  getDeptLabel(dept: string): string {
    switch (dept) {
      case 'PnC': return 'P&C';
      case 'QA':  return 'Q&A (Questionnaire)';
      default:    return dept;
    }
  }

  // ================= Drag-drop reordering =================

  onRowReorder(event: TableRowReorderEvent): void {
    const dragIndex = event.dragIndex;
    const dropIndex = event.dropIndex;
    if (dragIndex === undefined || dropIndex === undefined) return;
    if (dragIndex === dropIndex) return;

    const before = this.currentTabTasks;
    const moving = before[dragIndex];
    if (!moving) return;

    const reordered = [...before];
    reordered.splice(dragIndex, 1);
    reordered.splice(dropIndex, 0, moving);

    const updates: OffboardingTaskTemplate[] = [];
    reordered.forEach((task, newIdx) => {
      if (task.sortOrder !== newIdx) {
        task.sortOrder = newIdx;
        updates.push(task);
      }
    });

    if (updates.length === 0) return;

    this.reordering = true;
    forkJoin(updates.map(u => this.offboardingService.updateTaskTemplate(u.id, u)))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (results) => {
          results.forEach(r => {
            const idx = this.templates.findIndex(t => t.id === r.id);
            if (idx >= 0) this.templates[idx] = r;
          });
          this.reordering = false;
          this.messageService.add({ severity: 'success', summary: 'Reordered', detail: `${results.length} task${results.length > 1 ? 's' : ''} updated`, life: 1500 });
        },
        error: () => {
          this.reordering = false;
          this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to save new order. Reloading…' });
          this.loadTemplates();
        }
      });
  }

  // ================= Bulk selection =================

  isSelected(id: string): boolean {
    return this.selectedIds.includes(id);
  }

  isAllVisibleSelected(): boolean {
    const visible = this.currentTabTasks;
    return visible.length > 0 && visible.every(t => this.selectedIds.includes(t.id));
  }

  toggleSelect(id: string): void {
    const idx = this.selectedIds.indexOf(id);
    if (idx >= 0) this.selectedIds.splice(idx, 1);
    else this.selectedIds.push(id);
  }

  toggleSelectAllVisible(checked: boolean): void {
    const visibleIds = this.currentTabTasks.map(t => t.id);
    if (checked) {
      this.selectedIds = Array.from(new Set([...this.selectedIds, ...visibleIds]));
    } else {
      this.selectedIds = this.selectedIds.filter(id => !visibleIds.includes(id));
    }
  }

  clearSelection(): void {
    this.selectedIds = [];
  }

  deleteSelected(): void {
    if (this.selectedIds.length === 0) return;

    const count = this.selectedIds.length;
    this.confirmationService.confirm({
      header: 'Delete Selected Tasks',
      message: `Delete ${count} selected task${count > 1 ? 's' : ''}? This cannot be undone. In-progress offboarding records that already have these tasks are not affected.`,
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Yes, delete',
      rejectLabel: 'Cancel',
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-text',
      accept: () => {
        this.deletingBulk = true;
        const ids = [...this.selectedIds];
        forkJoin(ids.map(id => this.offboardingService.deleteTaskTemplate(id)))
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () => {
              this.templates = this.templates.filter(t => !ids.includes(t.id));
              this.selectedIds = [];
              this.deletingBulk = false;
              this.messageService.add({ severity: 'success', summary: 'Deleted', detail: `${ids.length} task${ids.length > 1 ? 's' : ''} deleted` });
            },
            error: () => {
              this.deletingBulk = false;
              this.messageService.add({ severity: 'error', summary: 'Error', detail: 'One or more deletes failed. Reloading…' });
              this.loadTemplates();
            }
          });
      }
    });
  }

  // ================= Add / Edit dialog =================

  showAddDialog(): void {
    const def = this.tabs.find(t => t.key === this.activeTab);
    this.selectedTemplate = this.getEmpty();
    this.selectedTemplate.department = def?.department ?? 'PnC';
    // Restrict department dropdown when adding — only the tab we're on
    this.dialogDepartmentOptions = this.allDepartmentOptions.filter(o => o.value === def?.department);
    this.isEditMode = false;
    this.fieldTouched = {};
    this.displayDialog = true;
  }

  showEditDialog(t: OffboardingTaskTemplate): void {
    this.selectedTemplate = { ...t };
    // In edit mode expose all departments — admin may want to move a task
    this.dialogDepartmentOptions = [...this.allDepartmentOptions];
    this.isEditMode = true;
    this.fieldTouched = {};
    this.displayDialog = true;
  }

  hideDialog(): void {
    this.displayDialog = false;
  }

  saveTemplate(): void {
    this.fieldTouched['taskName'] = true;
    if (!this.selectedTemplate.taskName.trim()) return;

    if (this.isEditMode) {
      this.offboardingService.updateTaskTemplate(this.selectedTemplate.id, this.selectedTemplate)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (updated) => {
            const idx = this.templates.findIndex(t => t.id === updated.id);
            if (idx >= 0) this.templates[idx] = updated;
            this.messageService.add({ severity: 'success', summary: 'Updated' });
            this.hideDialog();
          },
          error: () => { this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to update' }); }
        });
    } else {
      // New task lands at the end of its department — auto-compute sortOrder so PMs never touch it
      const siblings = this.templates.filter(t => t.department === this.selectedTemplate.department);
      const nextOrder = siblings.length === 0
        ? 0
        : Math.max(...siblings.map(s => s.sortOrder ?? 0)) + 1;

      const dto: CreateOffboardingTaskTemplate = {
        taskName:  this.selectedTemplate.taskName,
        department: this.selectedTemplate.department,
        isDefault: this.selectedTemplate.isDefault,
        sortOrder: nextOrder
      };
      this.offboardingService.createTaskTemplate(dto)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (created) => {
            this.templates.push(created);
            this.messageService.add({ severity: 'success', summary: 'Created' });
            this.hideDialog();
          },
          error: () => { this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to create' }); }
        });
    }
  }

  // ================= Row actions =================

  toggleActive(t: OffboardingTaskTemplate): void {
    const activating = !t.isDefault;
    this.confirmationService.confirm({
      message: `Are you sure you want to ${activating ? 'activate' : 'deactivate'} "${t.taskName}"?`,
      header: activating ? 'Confirm Activate' : 'Confirm Deactivate',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: activating ? 'p-button-success' : 'p-button-danger',
      accept: () => {
    const updated = { ...t, isDefault: !t.isDefault };
    this.offboardingService.updateTaskTemplate(t.id, updated)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (returned) => {
          const idx = this.templates.findIndex(tmpl => tmpl.id === returned.id);
          if (idx >= 0) this.templates[idx] = returned;
          this.messageService.add({ severity: 'success', summary: returned.isDefault ? 'Activated' : 'Deactivated' });
        },
        error: () => { this.messageService.add({ severity: 'error', summary: 'Error' }); }
      });
      }
    });
  }

  deleteTemplate(t: OffboardingTaskTemplate): void {
    this.confirmationService.confirm({
      header: 'Confirm Delete',
      message: `Delete "${t.taskName}"?`,
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.offboardingService.deleteTaskTemplate(t.id)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () => {
              this.templates = this.templates.filter(tmpl => tmpl.id !== t.id);
              this.messageService.add({ severity: 'success', summary: 'Deleted' });
            },
            error: () => { this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to delete' }); }
          });
      }
    });
  }

  seedDefaults(): void {
    this.confirmationService.confirm({
      header: 'Seed Defaults',
      message: 'Seed default offboarding tasks? Only works if the templates table is empty.',
      icon: 'pi pi-info-circle',
      accept: () => {
        this.offboardingService.seedDefaultTemplates()
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () => { this.messageService.add({ severity: 'success', summary: 'Seeded' }); this.loadTemplates(); },
            error: () => { this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to seed' }); }
          });
      }
    });
  }

  private getEmpty(): OffboardingTaskTemplate {
    return { id: '', taskName: '', department: 'PnC', isDefault: true, sortOrder: 0 };
  }
}
